<template>
	<div class="additional-properties__container additional-properties__container--span">
		<label>Additional properties</label>
		<div
			v-if="fakerMethodsOptions.length > 0"
			class="additional-properties__property-container">
			<Checkbox
				v-model="isRequired"
				:binary="true"
				:disabled="title" />
			<div class="additional-properties__label-container">
				<div class="additional-properties__label">Required</div>
				<div class="additional-properties__description">
					Should this column be required when creating or updating the entity
				</div>
			</div>
		</div>
		<div class="additional-properties__property-container">
			<Checkbox
				v-model="title"
				:binary="true" />
			<div class="additional-properties__label-container">
				<div class="additional-properties__label">Title</div>
				<div class="additional-properties__description">
					This column will be used as the primary identifier in selects, tables and other contexts.
				</div>
			</div>
		</div>
		<div
			v-if="false"
			class="additional-properties__property-container">
			<Checkbox
				v-model="subtitle"
				:binary="true" />
			<div class="additional-properties__label-container">
				<div class="additional-properties__label">Subtitle</div>
				<div class="additional-properties__description">
					This column will be used as the secondary identifier for a specific entity of this module
					in selects, tables and other contexts.
				</div>
			</div>
		</div>
		<div class="additional-properties__property-container">
			<Checkbox
				v-model="column.searchable"
				:binary="true" />
			<div class="additional-properties__label-container">
				<div class="additional-properties__label">Searchable</div>
				<div class="additional-properties__description">
					This column should be used when searching for entities of this module.
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import Checkbox from 'primevue/checkbox'
import { computed } from 'vue'
import { supportedFakerMethods } from '@codecannon/schemas/interfaces/fakerMethods'
import type { EnrichedColumn } from '@codecannon/schemas/schemas/EnrichedColumn'
import type { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'

const props = defineProps<{
	column: EnrichedColumn
	module: EnrichedModule
}>()

const originalTitle = props.module.model.title

const fakerMethodsOptions = computed(() => {
	return supportedFakerMethods[props.column.type]?.supported ?? []
})

const isRequired = computed({
	get: getRequired,
	set: setRequired,
})

const title = computed({
	get: getTitle,
	set: setTitle,
})

const subtitle = computed({
	get: getSubtitle,
	set: setSubtitle,
})

function getRequired() {
	return getValidators().some((v) => v.columnValidator === 'required') || title.value
}

function setRequired(required: boolean) {
	if (title.value) return
	if (!props.column.typeConstraints) props.column.typeConstraints = []

	const requiredIndex = props.column.typeConstraints.find((v) => v.name === 'required')

	if (required) {
		if (requiredIndex) return
		// Add required validator
		props.column.typeConstraints.push({
			type: 'validator',
			name: 'required',
			params: [],
		})
	} else {
		const validatorIndex = props.column.typeConstraints.findIndex((v) => v.name === 'required')
		if (validatorIndex === -1) return
		props.column.typeConstraints = props.column.typeConstraints?.toSpliced(validatorIndex, 1)
	}
}

function getValidators() {
	return (props.column.typeConstraints?.filter((v) => v.type === 'validator') ?? []).map((v) => {
		// This is a simplified version - in the real implementation you'd need to map to actual ColumnValidator objects
		return { columnValidator: v.name }
	})
}

function getTitle() {
	return (
		props.module.model.title === props.column.name ||
		Object.values(props.module.model.columns).length === 0
	)
}

function setTitle(value: boolean) {
	if (value) {
		props.module.model.title = props.column.name
	} else {
		props.module.model.title = originalTitle
	}
}

function getSubtitle() {
	return (
		props.module.model.subtitle === props.column.name ||
		Object.values(props.module.model.columns).length === 0
	)
}

function setSubtitle() {
	props.module.model.subtitle = props.column.name
}
</script>

<style lang="scss" scoped>
.additional-properties__container {
	display: flex;
	flex-direction: column;
	gap: 5px;
	margin-bottom: 16px;

	&:last-of-type {
		margin-bottom: 0;
	}

	&--span {
		grid-column: 1 / -1;
	}
}

.additional-properties__property-container {
	display: flex;
	gap: 10px;
	align-items: center;
	margin-left: 10px;

	.additional-properties__label-container {
		display: flex;
		flex-direction: column;
		gap: 5px;

		.additional-properties__label {
			font-size: 14px;
			color: var(--p-text-color);
		}

		.additional-properties__description {
			font-size: 12px;
			color: var(--p-text-muted-color);
			margin-top: -4px;
		}
	}
}
</style>
